import { setOptions, importLibrary } from '@googlemaps/js-api-loader'
import { useEffect, useState } from 'react'

/** Stable reference — do not inline in components (triggers script reloads). */
export const GOOGLE_MAPS_LIBRARIES = ['geometry', 'places']

export const GOOGLE_MAPS_LOADER_ID = 'housefolios-google-maps'

/** Fallback dark roadmap styles when ColorScheme enum is unavailable. */
export const GOOGLE_MAP_DARK_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ color: '#757575' }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9e9e9e' }],
  },
  {
    featureType: 'administrative.land_parcel',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#bdbdbd' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#757575' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#181818' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#616161' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.fill',
    stylers: [{ color: '#2c2c2c' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#8a8a8a' }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [{ color: '#373737' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#3c3c3c' }],
  },
  {
    featureType: 'road.highway.controlled_access',
    elementType: 'geometry',
    stylers: [{ color: '#4e4e4e' }],
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#616161' }],
  },
  {
    featureType: 'transit',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#757575' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#000000' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#3d3d3d' }],
  },
]

let mapsReadyPromise = null
let loaderConfigured = false

function configureMapsLoader() {
  if (loaderConfigured) return
  setOptions({
    key: import.meta.env.VITE_GOOGLE_APIKEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  })
  loaderConfigured = true
}

async function importMapsLibraries() {
  if (typeof window === 'undefined') {
    throw new Error('Google Maps requires a browser environment')
  }

  if (typeof window.google?.maps?.importLibrary === 'function') {
    await Promise.all([
      window.google.maps.importLibrary('places'),
      window.google.maps.importLibrary('geometry'),
      window.google.maps.importLibrary('core'),
    ])
    return window.google.maps
  }

  configureMapsLoader()
  await Promise.all([
    importLibrary('places'),
    importLibrary('geometry'),
    importLibrary('core'),
  ])
  return window.google.maps
}

/**
 * Ensures Maps JS is bootstrapped with geometry + places (same libraries as
 * useGoogleMapsApi). Prefer calling useGoogleMapsApi in React UIs so the
 * script loads once; use this for non-hook callers (e.g. Street View helpers).
 */
export function ensureMapsReady() {
  if (!mapsReadyPromise) {
    mapsReadyPromise = importMapsLibraries().catch((err) => {
      mapsReadyPromise = null
      throw err
    })
  }
  return mapsReadyPromise
}

/**
 * Ensures Places (New) is available, including PlaceAutocompleteElement.
 * Uses @googlemaps/js-api-loader's bootstrap so google.maps.importLibrary exists —
 * classic <script loading=async> tags do not reliably install it.
 */
export function ensurePlacesLibrary() {
  return ensureMapsReady().then((maps) => maps.places)
}

/**
 * Shared Maps JS loader. Call from any map / places UI so the script
 * loads once with geometry + places via js-api-loader 2.x.
 */
export function useGoogleMapsApi() {
  const alreadyLoaded =
    typeof window !== 'undefined' && Boolean(window.google?.maps)
  const [isLoaded, setIsLoaded] = useState(alreadyLoaded)
  const [loadError, setLoadError] = useState(undefined)

  useEffect(() => {
    let cancelled = false
    ensureMapsReady()
      .then(() => {
        if (!cancelled) setIsLoaded(true)
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { isLoaded, loadError }
}
